/**
 * Navbar.jsx
 * Top navigation bar for QuitPact.
 *
 * Responsibilities:
 *   - Brand logo / name (links back to home)
 *   - Guest state: shows "Sign In" link (right-aligned)
 *   - Fully responsive: collapses to hamburger on mobile
 *   - Keyboard accessible: all links reachable via Tab
 *
 * AC coverage:
 *   - AC 1.1.2 (Navigation Handling): navbar does NOT duplicate the CTA;
 *     the hero section owns the single primary action.
 *   - AC - 3.1.1 navigation: Cost Calculator under Explore dropdown.
 *   - AC - 3.2.1 navigation: Health Awareness under Explore dropdown.
 *   - AC - 3.2.2 navigation: Quit Benefits under Explore dropdown.
 *
 * Team: TA37 - The Coders' Assembly
 */

import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import "./Navbar.css";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const closeMenu = () => {
    setMenuOpen(false);
    setExploreOpen(false);
  };

  const toggleExplore = () => setExploreOpen((prev) => !prev);

  return (
    <header className="navbar" role="banner">
      <nav className="navbar__inner" aria-label="Main navigation">
        <NavLink
          to="/"
          className="navbar__brand"
          aria-label="QuitPact - go to homepage"
          onClick={closeMenu}
        >
          <img
            src="/logo.png"
            alt="QuitPact logo"
            className="navbar__logo-img"
          />
        </NavLink>

        <button
          className={`navbar__hamburger ${menuOpen ? "navbar__hamburger--open" : ""}`}
          onClick={toggleMenu}
          aria-expanded={menuOpen}
          aria-controls="navbar-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          type="button"
        >
          <span />
          <span />
          <span />
        </button>

        <ul
          id="navbar-menu"
          className={`navbar__links ${menuOpen ? "navbar__links--open" : ""}`}
        >
          <li>
            <NavLink
              to="/"
              className={({ isActive }) =>
                `navbar__link ${isActive ? "navbar__link--active" : ""}`
              }
              onClick={closeMenu}
              end
            >
              Home
            </NavLink>
          </li>

          <li>
            <NavLink
              to="/check-in"
              className={({ isActive }) =>
                `navbar__link ${isActive ? "navbar__link--active" : ""}`
              }
              onClick={closeMenu}
            >
              Check-In
            </NavLink>
          </li>

          <li className="navbar__dropdown">
            <button
              className="navbar__link navbar__dropdown-toggle"
              onClick={toggleExplore}
              aria-expanded={exploreOpen}
              type="button"
            >
              Explore {exploreOpen ? "▴" : "▾"}
            </button>

            {exploreOpen && (
              <ul className="navbar__dropdown-menu">
                <li>
                  <NavLink
                    to="/cost-calculator"
                    className="navbar__dropdown-item"
                    onClick={closeMenu}
                  >
                    Cost Calculator
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/health-awareness"
                    className="navbar__dropdown-item"
                    onClick={closeMenu}
                  >
                    Health Awareness
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/quit-benefits"
                    className="navbar__dropdown-item"
                    onClick={closeMenu}
                  >
                    Quit Benefits
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/smoking-story"
                    className="navbar__dropdown-item"
                    onClick={closeMenu}
                  >
                    Smokes & Stats
                  </NavLink>
                </li>
              </ul>
            )}
          </li>
        </ul>
      </nav>
    </header>
  );
};

export default Navbar;
